import {
  DEFAULT_IGLOO_STYLE,
  IGLOO_FLOORS,
  IGLOO_WALLS,
  WORLD,
  parseIglooArt,
  type IglooStyle,
} from "@clubap/shared";
import { Container, Graphics } from "pixi.js";
import { WALL, windows } from "./common.js";

/**
 * El iglú: el cuarto propio, dibujado con el piso y la pared que eligió su dueño.
 *
 * Es el único escenario del juego que NO es una función fija: los demás son un sitio del
 * campus, y éste es tantos como combinaciones haya. Por eso no vive en
 * `PROCEDURAL_SCENES` sino que se resuelve desde la clave de arte, que lleva el estilo
 * dentro (`igloo:piso:pared`).
 *
 * Lo que cambia es SÓLO el color. El piso caminable, la colisión y la puerta salen de
 * `IGLOO_FLOOR` en shared y no se enteran de esto — una pared de ladrillo no estrecha el
 * cuarto.
 *
 * A propósito sigue siendo sobrio: lo que llena un iglú son los muebles de su dueño, y un
 * fondo cargado competiría con ellos.
 */
export function iglooScene(style: IglooStyle = DEFAULT_IGLOO_STYLE): Container {
  const floor = IGLOO_FLOORS[style.floor];
  const wall = IGLOO_WALLS[style.wall];

  const scene = new Container();

  const paredes = new Graphics();
  paredes.rect(0, 0, WORLD.width, WALL).fill(wall.color);
  paredes.rect(0, WALL - 12, WORLD.width, 12).fill(wall.trim);
  scene.addChild(paredes);

  const piso = new Graphics();
  piso.rect(0, WALL, WORLD.width, WORLD.height - WALL).fill(floor.color);

  if (floor.cuadros) {
    /* Un damero se dibuja pintando SÓLO los cuadros oscuros sobre el fondo claro: la
       mitad de rectángulos y ni una línea que cuadrar. */
    const paso = 60;
    for (let fila = 0; (WALL + fila * paso) < WORLD.height; fila++) {
      for (let col = 0; col * paso < WORLD.width; col++) {
        if ((fila + col) % 2 === 0) continue;
        piso.rect(col * paso, WALL + fila * paso, paso, paso);
      }
    }
    piso.fill(floor.trim);
  } else {
    // Liso: unas juntas en perspectiva, que es lo que hace que se lea como suelo.
    for (let i = 0; i <= 12; i++) {
      const x = (WORLD.width / 12) * i;
      piso.moveTo(x, WALL).lineTo(x + (x - WORLD.width / 2) * 0.35, WORLD.height);
    }
    piso.stroke({ width: 1, color: floor.trim, alpha: 0.75 });
  }

  scene.addChild(piso);

  // Las ventanas se tiñen con el detalle de la pared para que no floten sobre ella.
  windows(scene, 2, 70, tono(wall.trim));

  return scene;
}

/**
 * De `#rrggbb` al número que piden las piezas compartidas de `common.ts`.
 *
 * El catálogo guarda texto porque el mismo color lo usa el panel de estilos, que es DOM
 * y necesita CSS. Convertir acá es más barato que guardar el color dos veces.
 */
function tono(hex: string): number {
  return Number.parseInt(hex.replace("#", ""), 16);
}

/**
 * El escenario que pide una clave de arte, si esa clave es de un iglú.
 *
 * Devuelve `null` para cualquier otra cosa, que es como el registro de escenarios sabe
 * que no le toca a esto.
 */
export function iglooSceneFor(artKey: string): Container | null {
  const style = parseIglooArt(artKey);
  return style ? iglooScene(style) : null;
}
